// src/pages/PrescriptionsClinic/index.js
//
// Reçeteler — klinik geneli reçete listesi (ilacı olan veya reçete talebi açık
// tedaviler). Düz liste (docs/design-principles.md §3): arama → durum çipleri →
// açık talep şeridi → çerçevesiz tablo. Satıra tıklayınca sağdan açılan drawer'da
// PrescriptionEditor. Düzenleme yetkisi "prescriptions:manage".

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  LocalPharmacy as LocalPharmacyIcon,
  HourglassEmpty as HourglassIcon,
} from "@mui/icons-material";

import PrescriptionEditor from "../../components/organisms/PatientWorkspaceTabs/PrescriptionEditor";
import useAuth from "../../hooks/useAuth";
import useTerms from "../../hooks/useTerms";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useAlert } from "../../context/AlertProvider";
import { useListClinicPrescriptionsMutation } from "../../store/secretaryApi";
import { useListMedicineTemplatesMutation } from "../../store/systemVariablesApi";
import {
  ClinicRecordListLayout,
  RecordEditorDrawer,
  SORTERS,
  patientNameOf,
  useClientList,
} from "../../components/organisms/ClinicRecordList";
import { fullDateTime } from "../Secretary/PatientsPage/patientListFormat";
import PrescriptionsTable, { dateOf } from "./PrescriptionsTable";
import { PRESCRIPTION_STATUS, getPrescriptionStatus, diagnosisNames, medicationNames } from "./constants";

const SEGMENT_EMPTY = {
  written: "Yazılmış reçete yok.",
  requested: "Bekleyen reçete talebi yok.",
  pending: "İlaçsız ve talepsiz reçete kaydı yok.",
};

const matches = (t, text) =>
  patientNameOf(t.user).toLocaleLowerCase("tr").includes(text) ||
  String(t.user?.tcNo || "").includes(text) ||
  diagnosisNames(t).some((n) => n.toLocaleLowerCase("tr").includes(text)) ||
  medicationNames(t).some((n) => n.toLocaleLowerCase("tr").includes(text));

const SORT_FNS = { name: SORTERS.name((t) => t.user), date: SORTERS.date(dateOf) };
const DEFAULT_DIR = { name: "asc", date: "desc" };

export default function PrescriptionsClinic() {
  const terms = useTerms();
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { showError } = useAlert();
  const canManage = useHasPermission("prescriptions:manage");

  const [listClinicPrescriptions, { isLoading }] = useListClinicPrescriptionsMutation();
  const [listMedicineTemplates] = useListMedicineTemplatesMutation();

  const [treatments, setTreatments] = useState([]);
  const [medicineTemplates, setMedicineTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const res = await listClinicPrescriptions({ token }).unwrap();
      setTreatments(res?.data?.treatments || []);
    } catch (_) {
      showError("Reçeteler yüklenemedi.");
    } finally {
      setLoaded(true);
    }
  }, [token, listClinicPrescriptions, showError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // İlaç şablonlarını editor için yükle
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await listMedicineTemplates({ token, status: "active", limit: 500 }).unwrap();
        setMedicineTemplates(res?.data?.items || res?.data?.medicines || []);
      } catch (_) {
        /* sessiz */
      }
    })();
  }, [token, listMedicineTemplates]);

  const list = useClientList(treatments, {
    segmentOf: getPrescriptionStatus,
    matches,
    sorters: SORT_FNS,
    defaultDir: DEFAULT_DIR,
    initialSort: "date",
  });

  // "Bekliyor" yalnız böyle kayıt varken görünür (liste ilaçlı/talepli kayıtları getirir)
  const segments = useMemo(
    () =>
      [
        { value: "all", label: "Tümü" },
        { value: "written", label: PRESCRIPTION_STATUS.written.label },
        { value: "requested", label: PRESCRIPTION_STATUS.requested.label },
        list.counts.pending ? { value: "pending", label: PRESCRIPTION_STATUS.pending.label } : null,
      ]
        .filter(Boolean)
        .map((s) => ({ value: s.value, label: `${s.label} · ${list.counts[s.value] || 0}` })),
    [list.counts]
  );

  // Drawer'daki kaydet sonrası listeyi tazele ve seçili kaydı güncelle
  const handleSaved = useCallback((updated) => {
    if (!updated?._id) return;
    setTreatments((prev) => prev.map((r) => (r._id === updated._id ? { ...r, ...updated, user: r.user } : r)));
    setSelected((prev) => (prev && prev._id === updated._id ? { ...prev, ...updated, user: prev.user } : prev));
  }, []);

  const handleEditorError = useCallback(
    (err) => {
      const status = err?.status || err?.originalStatus;
      const serverMsg = err?.data?.message;
      if (status === 423) {
        showError(serverMsg || `Bu ${terms.patientLower} şu an ${terms.examLoc} — değişiklik kaydedilemedi.`);
        return;
      }
      showError(serverMsg || "Reçete kaydedilemedi.");
    },
    [showError, terms]
  );

  const requestedCount = treatments.filter((t) => getPrescriptionStatus(t) === "requested").length;
  const notice =
    requestedCount > 0 && list.segment !== "requested"
      ? {
          icon: <HourglassIcon />,
          text: (
            <>
              <strong>{requestedCount} reçete talebi</strong> yazılmayı bekliyor.
            </>
          ),
          actionLabel: "Görüntüle",
          onClick: () => list.setSegment("requested"),
        }
      : null;

  const empty = list.searchText
    ? { title: "Reçete bulunamadı", message: `“${list.searchText}” aramanızla eşleşen reçete yok. Aramayı değiştirin.` }
    : list.segment !== "all"
    ? { title: "Bu filtrede reçete yok", message: SEGMENT_EMPTY[list.segment] }
    : { title: "Henüz reçete yok", message: `Reçeteler ${terms.patientLower} detayındaki tedavi kayıtlarından yazılır.` };

  const selectedDiag = selected ? diagnosisNames(selected) : [];

  return (
    <>
      <ClinicRecordListLayout
        icon={<LocalPharmacyIcon sx={{ fontSize: 22 }} />}
        title="Reçeteler"
        subtitle="Yazılan reçeteler ve bekleyen reçete talepleri"
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder={`${terms.patient} adı, TC no, tanı veya ilaç ile ara`}
        segments={segments}
        segment={list.segment}
        onSegmentChange={list.setSegment}
        segmentAriaLabel="Reçete durumu"
        notice={notice}
      >
        <PrescriptionsTable list={list} isLoading={isLoading && !loaded} empty={empty} onOpenDetail={setSelected} />
      </ClinicRecordListLayout>

      <RecordEditorDrawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        icon={<LocalPharmacyIcon />}
        title={selected ? patientNameOf(selected.user) : "Reçete"}
        subtitle={selected ? `${selectedDiag[0] || "Reçete"} · ${fullDateTime(dateOf(selected))}` : ""}
        readOnly={!canManage}
        patientLink={selected?.user?._id ? `/Hastalar/${selected.user._id}?tab=receteler` : null}
      >
        {selected && (
          <PrescriptionEditor
            key={selected._id}
            record={selected}
            mode="standalone"
            patientId={selected.user?._id}
            token={token}
            canEdit={canManage}
            allMedicines={medicineTemplates}
            onSaved={handleSaved}
            onError={handleEditorError}
          />
        )}
      </RecordEditorDrawer>
    </>
  );
}
